import type {
  PricingCondition,
  PricingConditionGroup,
  PricingContext,
  CartPricingContext,
  ConditionType,
  MinQuantityConditionConfig,
  MinSessionsConditionConfig,
  SessionWindowConditionConfig,
  BookingAdvanceConditionConfig,
  DayOfWeekConditionConfig,
  TimeOfDayConditionConfig,
  MemberTagsConditionConfig,
  CapacityThresholdConditionConfig,
  TicketCombinationConditionConfig,
  MinSpendConditionConfig,
  ReturningCustomerConditionConfig,
  FirstTimeBookingConditionConfig,
  BookingCountInPeriodConditionConfig,
  ReferralSourceConditionConfig,
  DateRangeConditionConfig,
  SpecificDatesConditionConfig,
  ConsecutiveSessionsConditionConfig,
  RecurringDateRangeConditionConfig,
} from "./types";

type EvaluationContext = PricingContext | CartPricingContext;

export type ConditionEvalResult = {
  passes: boolean;
  failedCondition?: ConditionType;
  message?: string;
};

function isCartContext(ctx: EvaluationContext): ctx is CartPricingContext {
  return "totalCartAmount" in ctx;
}

function evaluateMinQuantity(config: MinQuantityConditionConfig, ctx: EvaluationContext): boolean {
  if (isCartContext(ctx)) {
    const totalQty = ctx.cart.reduce((sum, item) => sum + item.quantity, 0);
    return totalQty >= config.minQuantity;
  }
  return ctx.quantity >= config.minQuantity;
}

function evaluateMinSessions(config: MinSessionsConditionConfig, ctx: EvaluationContext): boolean {
  return ctx.sessionsCount >= config.minSessions;
}

function evaluateSessionWindow(config: SessionWindowConditionConfig, ctx: EvaluationContext): boolean {
  const sessions = isCartContext(ctx) ? ctx.selectedSessions : ctx.selectedSessions;
  if (!sessions || sessions.length === 0) return false;

  const dates = sessions.map((s) => new Date(s.date).getTime()).sort((a, b) => a - b);
  const spanDays = (dates[dates.length - 1] - dates[0]) / (1000 * 60 * 60 * 24);
  return spanDays <= config.timeWindowDays;
}

function evaluateBookingAdvance(config: BookingAdvanceConditionConfig, ctx: EvaluationContext): boolean {
  const sessionTime = new Date(ctx.sessionStartTime).getTime();
  const bookingTime = new Date(ctx.bookingTime).getTime();
  const daysBeforeEvent = (sessionTime - bookingTime) / (1000 * 60 * 60 * 24);
  return daysBeforeEvent >= config.daysBeforeEvent;
}

function evaluateLastMinute(config: { hoursBeforeEvent: number }, ctx: EvaluationContext): boolean {
  const sessionTime = new Date(ctx.sessionStartTime).getTime();
  const bookingTime = new Date(ctx.bookingTime).getTime();
  const hoursUntilEvent = (sessionTime - bookingTime) / (1000 * 60 * 60);
  return hoursUntilEvent >= 0 && hoursUntilEvent <= config.hoursBeforeEvent;
}

function evaluateDayOfWeek(config: DayOfWeekConditionConfig, ctx: EvaluationContext): boolean {
  return config.days.includes(ctx.sessionDayOfWeek);
}

function evaluateTimeOfDay(config: TimeOfDayConditionConfig, ctx: EvaluationContext): boolean {
  return ctx.sessionTimeOfDay >= config.startTime && ctx.sessionTimeOfDay <= config.endTime;
}

function evaluateMemberTags(config: MemberTagsConditionConfig, ctx: EvaluationContext): boolean {
  if (config.matchType === "any") {
    return config.requiredTags.some((t) => ctx.customerTags.includes(t));
  }
  return config.requiredTags.every((t) => ctx.customerTags.includes(t));
}

function evaluateCapacityThreshold(config: CapacityThresholdConditionConfig, ctx: EvaluationContext): boolean {
  if (isCartContext(ctx)) {
    if (ctx.currentSoldCount === undefined || ctx.sessionCapacity === undefined) return false;
    const soldPercentage = (ctx.currentSoldCount / ctx.sessionCapacity) * 100;
    return soldPercentage <= config.maxSoldPercentage;
  }
  return ctx.currentSoldCount !== undefined;
}

function evaluateTicketCombination(config: TicketCombinationConditionConfig, ctx: EvaluationContext): boolean {
  const cart = isCartContext(ctx) ? ctx.cart : ctx.cart;
  if (!cart) return false;
  const cartTicketIds = cart.filter((item) => item.quantity > 0).map((item) => item.ticketTypeId);
  return config.requiredTicketIds.every((id) => cartTicketIds.includes(id));
}

function evaluateMinSpend(config: MinSpendConditionConfig, ctx: EvaluationContext): boolean {
  if (isCartContext(ctx)) {
    return ctx.totalCartAmount >= config.minAmount;
  }
  return false;
}

function evaluateReturningCustomer(config: ReturningCustomerConditionConfig, ctx: EvaluationContext): boolean {
  // Both context shapes declare the field; `undefined` = buyer unknown => fail closed.
  const prev = ctx.customerPreviousBookings;
  if (prev === undefined) return false;
  return prev >= config.minPreviousBookings;
}

function evaluateFirstTimeBooking(_config: FirstTimeBookingConditionConfig, ctx: EvaluationContext): boolean {
  const prev = ctx.customerPreviousBookings;
  if (prev === undefined) return false;
  return prev === 0;
}

function evaluateBookingCountInPeriod(config: BookingCountInPeriodConditionConfig, ctx: EvaluationContext): boolean {
  // Buckets are keyed by the rule's own periodDays so rules with different
  // periods on one listing never read each other's count.
  const count = ctx.customerBookingsByPeriod?.[config.periodDays];
  if (count === undefined) return false;
  return count >= config.minBookings;
}

function evaluateReferralSource(config: ReferralSourceConditionConfig, ctx: EvaluationContext): boolean {
  const source = isCartContext(ctx) ? ctx.customerReferralSource : (ctx as any).customerReferralSource;
  if (!source) return false;
  return config.sources.includes(source);
}

function evaluateDateRange(config: DateRangeConditionConfig, ctx: EvaluationContext): boolean {
  const sessionDate =
    ctx.sessionDateISO ?? new Date(ctx.sessionStartTime).toISOString().slice(0, 10);
  return sessionDate >= config.startDate && sessionDate <= config.endDate;
}

function evaluateSpecificDates(config: SpecificDatesConditionConfig, ctx: EvaluationContext): boolean {
  const sessionDate =
    ctx.sessionDateISO ?? new Date(ctx.sessionStartTime).toISOString().slice(0, 10);
  return config.dates.includes(sessionDate);
}

function evaluateConsecutiveSessions(config: ConsecutiveSessionsConditionConfig, ctx: EvaluationContext): boolean {
  const sessions = isCartContext(ctx) ? ctx.selectedSessions : ctx.selectedSessions;
  if (!sessions || sessions.length < config.minConsecutive) return false;

  const dates = sessions.map((s) => new Date(s.date).getTime()).sort((a, b) => a - b);
  const oneWeek = 7 * 24 * 60 * 60 * 1000;
  let consecutive = 1;
  let maxConsecutive = 1;

  for (let i = 1; i < dates.length; i++) {
    const gap = dates[i] - dates[i - 1];
    if (gap >= oneWeek - 12 * 60 * 60 * 1000 && gap <= oneWeek + 12 * 60 * 60 * 1000) {
      consecutive++;
      maxConsecutive = Math.max(maxConsecutive, consecutive);
    } else {
      consecutive = 1;
    }
  }

  return maxConsecutive >= config.minConsecutive;
}

function evaluateRecurringDateRange(config: RecurringDateRangeConditionConfig, ctx: EvaluationContext): boolean {
  const session = new Date(ctx.sessionStartTime);
  // Prefer the school-timezone values when the caller normalised them (see
  // PricingContext.sessionMonth). Falling back to the runtime-local reading
  // keeps every other caller behaving exactly as before.
  // Both or neither. Taken independently, a caller that set one and not the
  // other would compare a school-timezone month against a runtime-local
  // day-of-month — wrong only across a midnight boundary, which is exactly the
  // case no test would catch. `priceRentalHire` sets both, so this is
  // unreachable by construction rather than by convention.
  const normalised = ctx.sessionMonth != null && ctx.sessionDayOfMonth != null;
  const month = normalised ? ctx.sessionMonth! : session.getMonth() + 1; // 1-12
  const day = normalised ? ctx.sessionDayOfMonth! : session.getDate();

  // Convert to comparable number: month * 100 + day (e.g. Jan 15 = 115, Mar 20 = 320)
  const sessionVal = month * 100 + day;
  const startVal = config.startMonth * 100 + config.startDay;
  const endVal = config.endMonth * 100 + config.endDay;

  if (startVal <= endVal) {
    // Normal range: e.g. 15 Jan to 20 Mar
    return sessionVal >= startVal && sessionVal <= endVal;
  }
  // Wrapping range: e.g. 15 Nov to 20 Feb
  return sessionVal >= startVal || sessionVal <= endVal;
}

function evaluateSingleCondition(condition: PricingCondition, ctx: EvaluationContext): boolean {
  const config = condition.config;
  switch (condition.conditionType) {
    case "MIN_QUANTITY":
      return evaluateMinQuantity(config as MinQuantityConditionConfig, ctx);
    case "MIN_SESSIONS":
      return evaluateMinSessions(config as MinSessionsConditionConfig, ctx);
    case "SESSION_WINDOW":
      return evaluateSessionWindow(config as SessionWindowConditionConfig, ctx);
    case "BOOKING_ADVANCE":
      return evaluateBookingAdvance(config as BookingAdvanceConditionConfig, ctx);
    case "LAST_MINUTE":
      return evaluateLastMinute(config as { hoursBeforeEvent: number }, ctx);
    case "DAY_OF_WEEK":
      return evaluateDayOfWeek(config as DayOfWeekConditionConfig, ctx);
    case "TIME_OF_DAY":
      return evaluateTimeOfDay(config as TimeOfDayConditionConfig, ctx);
    case "MEMBER_TAGS":
      return evaluateMemberTags(config as MemberTagsConditionConfig, ctx);
    case "CAPACITY_THRESHOLD":
      return evaluateCapacityThreshold(config as CapacityThresholdConditionConfig, ctx);
    case "TICKET_COMBINATION":
      return evaluateTicketCombination(config as TicketCombinationConditionConfig, ctx);
    case "MIN_SPEND":
      return evaluateMinSpend(config as MinSpendConditionConfig, ctx);
    case "RETURNING_CUSTOMER":
      return evaluateReturningCustomer(config as ReturningCustomerConditionConfig, ctx);
    case "FIRST_TIME_BOOKING":
      return evaluateFirstTimeBooking(config as FirstTimeBookingConditionConfig, ctx);
    case "BOOKING_COUNT_IN_PERIOD":
      return evaluateBookingCountInPeriod(config as BookingCountInPeriodConditionConfig, ctx);
    case "REFERRAL_SOURCE":
      return evaluateReferralSource(config as ReferralSourceConditionConfig, ctx);
    case "DATE_RANGE":
      return evaluateDateRange(config as DateRangeConditionConfig, ctx);
    case "SPECIFIC_DATES":
      return evaluateSpecificDates(config as SpecificDatesConditionConfig, ctx);
    case "CONSECUTIVE_SESSIONS":
      return evaluateConsecutiveSessions(config as ConsecutiveSessionsConditionConfig, ctx);
    case "MONTH_RANGE":
      return evaluateRecurringDateRange(config as RecurringDateRangeConditionConfig, ctx);
    default:
      return false;
  }
}

export function evaluateConditions(
  conditions: PricingCondition[],
  ctx: EvaluationContext,
): ConditionEvalResult {
  for (const condition of conditions) {
    if (!evaluateSingleCondition(condition, ctx)) {
      return { passes: false, failedCondition: condition.conditionType };
    }
  }
  return { passes: true };
}

export type ConditionGroupsEvalResult = {
  passes: boolean;
  failedGroupIndex?: number;
};

/** The groups a rule is actually gated on: modern `conditionGroups` when
 *  present, otherwise the legacy flat `conditions` list wrapped as one group. */
function resolveConditionGroups(
  groups: PricingConditionGroup[] | undefined,
  legacyConditions: PricingCondition[] | undefined,
): PricingConditionGroup[] {
  return groups?.length
    ? groups
    : legacyConditions?.length
      ? [{ id: "legacy", conditions: legacyConditions }]
      : [];
}

/** True when `evaluateConditionGroups` can ever fail for this rule: there is
 *  at least one group and none of them is empty (an empty group passes all). */
export function hasEvaluableConditions(
  groups: PricingConditionGroup[] | undefined,
  legacyConditions: PricingCondition[] | undefined,
): boolean {
  const resolved = resolveConditionGroups(groups, legacyConditions);
  return resolved.length > 0 && resolved.every((g) => g.conditions.length > 0);
}

export function evaluateConditionGroups(
  groups: PricingConditionGroup[] | undefined,
  legacyConditions: PricingCondition[] | undefined,
  ctx: EvaluationContext,
): ConditionGroupsEvalResult {
  const resolved = resolveConditionGroups(groups, legacyConditions);

  if (resolved.length === 0) return { passes: true };

  let lastFailedIndex = -1;
  for (let i = 0; i < resolved.length; i++) {
    const group = resolved[i]!;
    if (group.conditions.length === 0) return { passes: true };
    if (evaluateConditions(group.conditions, ctx).passes) return { passes: true };
    lastFailedIndex = i;
  }
  return { passes: false, failedGroupIndex: lastFailedIndex };
}
